import axios from 'axios';
import { useCallback, useMemo } from 'react';

import type { VideoRoomJournalPort, VideoRoomJournalSnapshot, VideoRoomSessionSnapshot } from '@zingizi/video-room';

import { consultationService } from '../../configs/api/api.config';
import { store } from '../../store/store';
import type { IConsultation, IJournalResult } from '../../store/types';

interface UseZingiziJournalAdapterArgs {
  consultation?: IConsultation;
  isPsychologist: boolean;
  isGroupConsultationFlag: boolean;
  groupParticipantNamesRef: React.MutableRefObject<Record<string, string>>;
}

function createEmptyJournalResult(): IJournalResult {
  return {
    questions: [],
    dpdg: { date_time: '', comment: '' },
    emotions: [],
    mac_cards: [],
    wheel: [],
    color_code: [],
    clouds: [],
    result: '',
  };
}

/** Перечислять поля нельзя — забытое молча стирается при сохранении (см. sand_therapy). */
function mapJournalResultToSnapshot(result: IJournalResult): VideoRoomJournalSnapshot {
  const { result: summary, ...tools } = result;

  return { tools, summary };
}

function mapSnapshotToJournalResult(snapshot: VideoRoomJournalSnapshot | undefined): IJournalResult {
  if (!snapshot) {
    return createEmptyJournalResult();
  }

  return {
    ...createEmptyJournalResult(),
    ...((snapshot.tools ?? {}) as Partial<IJournalResult>),
    result: snapshot.summary ?? '',
  };
}

export function useZingiziJournalAdapter({
  consultation,
  isPsychologist,
  isGroupConsultationFlag,
  groupParticipantNamesRef,
}: UseZingiziJournalAdapterArgs): VideoRoomJournalPort {
  const load = useCallback(
    (_session: VideoRoomSessionSnapshot) =>
      Promise.resolve(mapJournalResultToSnapshot(consultation?.result?.[0] ?? createEmptyJournalResult())),
    [consultation?.result],
  );

  const save = useCallback(
    async (_session: VideoRoomSessionSnapshot, snapshot: VideoRoomJournalSnapshot) => {
      const getToken = () => localStorage.getItem('tokensZZ');

      if (!consultation || !isPsychologist) {
        return;
      }

      let payload = mapSnapshotToJournalResult(snapshot);

      if (isGroupConsultationFlag) {
        const state = store.getState();
        const macByParticipant = state.macStateSlice?.clientSelectionsByParticipant ?? {};
        const emotionsByParticipant = state.emotionsGroupSlice?.clientSelectionsByParticipant ?? {};
        const participantNames = groupParticipantNamesRef.current ?? {};

        const mac_cards_by_participant: Record<string, { date_time: string; cards: string[]; comment: string }[]> = {};

        for (const [identity, cards] of Object.entries(macByParticipant)) {
          if (cards?.length) {
            mac_cards_by_participant[identity] = [
              {
                date_time: new Date().toISOString(),
                cards: cards.map((card: { img: string }) => card.img),
                comment: '',
              },
            ];
          }
        }

        const emoToolSettings =
          store.getState().psyToolsSlice?.tools?.find((tool: { name: string }) => tool.name === 'emotions_detection')
            ?.psy_tool_settings ?? [];

        const emotions_by_participant: Record<
          string,
          { date_time: string; comment: string; emotions: { category: string; items: string[] }[] }[]
        > = {};

        for (const [identity, selection] of Object.entries(emotionsByParticipant)) {
          if (selection?.emo?.length) {
            const emotionsByCategory = (selection.category ?? [])
              .map((categoryName: string) => {
                const categoryItems: string[] =
                  (
                    emoToolSettings.find((setting: { name: string }) => setting.name === categoryName) as
                      | { value?: string[] }
                      | undefined
                  )?.value ?? [];
                const items = categoryItems.filter((item: string) => selection.emo.includes(item));

                return { category: categoryName, items };
              })
              .filter((emotion: { items: string[] }) => emotion.items.length > 0);

            emotions_by_participant[identity] = [
              {
                date_time: new Date().toISOString(),
                comment: '',
                emotions:
                  emotionsByCategory.length > 0 ? emotionsByCategory : [{ category: 'Эмоции', items: selection.emo }],
              },
            ];
          }
        }

        if (Object.keys(mac_cards_by_participant).length > 0 || Object.keys(emotions_by_participant).length > 0) {
          payload = {
            ...payload,
            mac_cards_by_participant: Object.keys(mac_cards_by_participant).length
              ? mac_cards_by_participant
              : payload.mac_cards_by_participant,
            emotions_by_participant: Object.keys(emotions_by_participant).length
              ? emotions_by_participant
              : payload.emotions_by_participant,
            group_participant_names: Object.keys(participantNames).length
              ? participantNames
              : payload.group_participant_names,
          };
        }
      }

      await axios.put(
        `${consultationService}/consultations/${consultation.id}`,
        { result: [payload] },
        {
          headers: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            Authorization: `Bearer ${JSON.parse(getToken()).token}`,
          },
        },
      );
    },
    [consultation, groupParticipantNamesRef, isGroupConsultationFlag, isPsychologist],
  );

  return useMemo(
    () => ({
      load,
      save,
    }),
    [load, save],
  );
}
